import { useMutation, useQuery } from '@tanstack/react-query';
import { Alert, Button, Switch, Typography } from 'antd';
import axios from 'axios';
import React from 'react';
import { apiClient } from '@api/client';
import { figmaSettingsQuery } from '@helpers/figmaPosting';
import { IntegrationsFigmaIcon } from '@icons';
import {
  FigmaPluginSteps,
  PostingAccountRow
} from '@pages/AdminSettings/components/IntegrationTab/figma-shared';
import type { IntegrationInstallInstructionsProps } from '@pages/AdminSettings/components/IntegrationTab/constants';
import {
  SetupHelp,
  SetupLayout,
  SetupSection
} from '@pages/AdminSettings/components/IntegrationTab/setup-layout';

const { Text } = Typography;

const INSTALL_ERRORS: Record<string, string> = {
  install_not_allowed: 'Only organization admins can install integrations.',
  plan_not_allowed:
    'Your organization’s plan doesn’t include the Figma integration.'
};

/**
 * Installing Figma for the organization (an admin): whether comments go to
 * Figma, an optional posting account, then the plugin for designers, who
 * connect their own Figma accounts in it.
 */
export default function FigmaInstallInstructions({
  organizationId,
  installed = false,
  onClose
}: IntegrationInstallInstructionsProps) {
  // Installed already: the plugin step only (read once, as installing here
  // makes it installed too).
  const [alreadyInstalled] = React.useState(installed);
  const [postComments, setPostComments] = React.useState(true);
  const [step, setStep] = React.useState<'install' | 'posting' | 'plugin'>(
    alreadyInstalled ? 'plugin' : 'install'
  );
  const install = useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.figma.figmaControllerInstall({
        organizationId,
        postComments
      });
      return data.integrationId;
    },
    onSuccess: () => setStep(postComments ? 'posting' : 'plugin')
  });
  const installError = (() => {
    if (!install.isError) return null;
    const code = axios.isAxiosError(install.error)
      ? (install.error.response?.data as { errorCode?: string })?.errorCode
      : undefined;
    return (
      (code && INSTALL_ERRORS[code]) ??
      'Couldn’t install Figma. Check your connection and try again.'
    );
  })();
  const settings = useQuery({
    ...figmaSettingsQuery(install.data ?? ''),
    enabled: step === 'posting' && !!install.data
  });

  const steps = [
    { key: 'install', title: 'Install' },
    ...(postComments ? [{ key: 'posting', title: 'Posting account' }] : []),
    { key: 'plugin', title: 'Plugin' }
  ];

  const footer = {
    install: (
      <>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          type="primary"
          loading={install.isLoading}
          onClick={() => install.mutate()}
        >
          Install
        </Button>
      </>
    ),
    posting: (
      <Button type="primary" onClick={() => setStep('plugin')}>
        {settings.data?.postingAccount ? 'Continue' : 'Skip for now'}
      </Button>
    ),
    plugin: (
      <Button type="primary" onClick={onClose}>
        Done
      </Button>
    )
  }[step];

  return (
    <SetupLayout
      logo={IntegrationsFigmaIcon}
      title={alreadyInstalled ? 'StreamWork plugin for Figma' : 'Set up Figma'}
      subtitle="Designers send Figma frames to StreamWork for review, and comments sync both ways."
      progress={
        alreadyInstalled
          ? undefined
          : {
              current: steps.findIndex((s) => s.key === step),
              items: steps.map((s) => s.title)
            }
      }
      footer={footer}
    >
      {step === 'install' && (
        <SetupSection>
          <div className="integration_setup-card">
            <div className="integration_setup-row">
              <div className="integration_setup-row-text">
                <Text strong id="figma-install-post-comments">
                  Post comments to Figma
                </Text>
                <Text type="secondary">
                  Comments are always imported from Figma. With this on,
                  StreamWork comments on media exported from Figma, and replies
                  to Figma threads, also show up in the Figma file. You can
                  change it later.
                </Text>
              </div>
              <div className="integration_setup-row-control">
                <Switch
                  aria-labelledby="figma-install-post-comments"
                  checked={postComments}
                  onChange={setPostComments}
                />
              </div>
            </div>
          </div>
          {installError && (
            <Alert type="error" showIcon message={installError} />
          )}
        </SetupSection>
      )}

      {step === 'posting' && install.data && (
        <SetupSection
          title="Posting account"
          description="People who connected their own Figma account post their comments as themselves. For everyone else, a posting account can post them, with their name on it. You can add or change it later in the integration's details."
        >
          <div className="integration_setup-card">
            {settings.data ? (
              <PostingAccountRow
                integrationId={install.data}
                settings={settings.data}
                onChange={() => settings.refetch()}
              />
            ) : (
              <div className="figma_details-empty">
                <Text type="secondary">Loading…</Text>
              </div>
            )}
          </div>
        </SetupSection>
      )}

      {step === 'plugin' && (
        <>
          <Alert
            type={alreadyInstalled ? 'info' : 'success'}
            showIcon
            message={
              alreadyInstalled
                ? 'Figma is installed for this organization'
                : 'Figma is installed'
            }
            description="Share the plugin with your designers. Each of them connects their own Figma account in it. Settings are in the integration's details under Active."
          />
          <FigmaPluginSteps />
          <SetupHelp subject="the plugin" />
        </>
      )}
    </SetupLayout>
  );
}
